import React, { useState } from "react";
import { Link } from "react-router-dom";

function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleQuestion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // EDIT THIS ARRAY TO ADD/REMOVE/MODIFY QUESTIONS AND ANSWERS
  const faqs = [
    {
      category: "General Questions",
      questions: [
        {
          question: "What is PlasAnn?",
          answer: "PlasAnn is a comprehensive plasmid annotation database and tool designed to help researchers identify, annotate, and analyze plasmid sequences. Our platform provides automated annotation services combined with curated data from the scientific community."
        },
        {
          question: "Is PlasAnn free to use?",
          answer: "Yes, PlasAnn is completely free for academic and research purposes. We are committed to supporting the scientific community by providing open access to plasmid annotation resources."
        },
        {
          question: "Who can use PlasAnn?",
          answer: "PlasAnn is designed for researchers, students, and scientists working with plasmids in molecular biology, microbiology, synthetic biology, and related fields. Anyone conducting plasmid research can benefit from our tools and database."
        }
      ]
    },
    {
      category: "Using PlasAnn",
      questions: [
        {
          question: "How do I submit a plasmid for annotation?",
          answer: "You can submit plasmids through our Contact page by selecting 'Submit Annotation' and choosing 'Whole Annotated Plasmid'. Fill in the required information and upload your plasmid file in GenBank (.gb), CSV, TSV, or GFF format."
        },
        {
          question: "What file formats does PlasAnn accept?",
          answer: "For whole plasmid submissions, we accept: .gb, .gbk, .csv, .tsv, and .gff files. For single feature submissions, we accept: .gb, .gbk, .fasta, and .fa files. All files should be under 10MB."
        },
        {
          question: "How long does annotation take?",
          answer: "Automated annotation is typically completed within minutes. Manual curation and community-submitted annotations are reviewed by our team within 2-3 business days."
        },
        {
          question: "Can I submit a single feature instead of a whole plasmid?",
          answer: "Yes! We accept both whole plasmid submissions and individual feature annotations. Use the 'Single Annotated Feature' option on our submission form to contribute specific features like promoters, terminators, or coding sequences."
        }
      ]
    },
    {
      category: "Technical Questions",
      questions: [
        {
          question: "What types of features does PlasAnn annotate?",
          answer: "PlasAnn can identify and annotate various features including: Coding Sequences (CDS), promoters, terminators, origins of replication, resistance markers, ribosome binding sites (RBS), enhancers, and operators."
        },
        {
          question: "How accurate is the automated annotation?",
          answer: "Our automated annotation system uses state-of-the-art bioinformatics algorithms and is regularly updated with new data. However, we always recommend manual verification of important features and encourage community validation through our submission system."
        },
        {
          question: "What validation methods are supported?",
          answer: "We track annotations validated through: Experimental verification, Computational prediction, Literature references, and Published data. We encourage submitters to specify their validation method to help other researchers assess annotation confidence."
        },
        {
          question: "Can I download annotated plasmids?",
          answer: "Yes, all annotated plasmids in our database can be downloaded in standard GenBank format. You can also export annotations in various formats compatible with other bioinformatics tools."
        }
      ]
    },
    {
      category: "Data and Privacy",
      questions: [
        {
          question: "How is my submitted data used?",
          answer: "Submitted plasmid annotations become part of our public database to benefit the research community. Your contact information is kept private and only used to communicate about your submission. We do not share personal information with third parties."
        },
        {
          question: "Can I submit proprietary or unpublished plasmids?",
          answer: "If you have proprietary plasmids, please contact us directly at plasanndevs@gmail.com to discuss confidentiality options. By default, submissions are made publicly available to support open science."
        },
        {
          question: "How do you handle citation and attribution?",
          answer: "We track PubMed IDs and GenBank accessions for all submissions. When you provide publication information, we link it to the annotation so proper credit is maintained. If your work leads to a publication, we appreciate citation of PlasAnn in your methods section."
        }
      ]
    },
    {
      category: "Troubleshooting",
      questions: [
        {
          question: "My file upload is failing. What should I do?",
          answer: "Ensure your file is under 10MB and in a supported format (.gb, .gbk, .csv, .tsv, .gff, .fasta, or .fa). Check that the file isn't corrupted by opening it in a text editor. If problems persist, contact us with details about the error message you're receiving."
        },
        {
          question: "I submitted an annotation but haven't heard back. What now?",
          answer: "We typically review submissions within 2-3 business days. Check your spam folder for our response. If you haven't heard from us after 5 business days, please email plasanndevs@gmail.com with your submission details."
        },
        {
          question: "I found an error in an annotation. How do I report it?",
          answer: "We appreciate error reports! You can submit a correction through our 'Single Annotated Feature' form with the corrected information, or contact us directly at plasanndevs@gmail.com with details about the error."
        }
      ]
    }
  ];

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div 
        className="text-white p-6 rounded-lg mb-6 shadow-lg"
        style={{
          background: 'linear-gradient(135deg, #354931 0%, #5d8156 50%, #86bb7c 100%)'
        }}
      >
        <h1 className="text-3xl font-bold mb-2">❓ Frequently Asked Questions</h1>
        <p style={{ color: '#c8d3c5' }}>
          Find answers to common questions about PlasAnn
        </p>
      </div>

      {/* Quick Navigation */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6 border border-[#d1ddd1]">
        <h2 className="text-xl font-bold text-[#4e5a4c] mb-4">Quick Navigation</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {faqs.map((category, idx) => (
            <a
              key={idx}
              href={`#${category.category.replace(/\s+/g, '-').toLowerCase()}`}
              className="text-[#6b7966] hover:text-[#4e5a4c] hover:underline text-sm"
            >
              📋 {category.category}
            </a>
          ))}
        </div>
      </div>

      {/* FAQ Categories */}
      {faqs.map((category, categoryIdx) => (
        <div 
          key={categoryIdx}
          id={category.category.replace(/\s+/g, '-').toLowerCase()}
          className="mb-8"
        >
          <h2 className="text-2xl font-bold text-[#4e5a4c] mb-4 border-b-2 border-[#6b7966] pb-2">
            {category.category}
          </h2>
          
          <div className="space-y-3">
            {category.questions.map((faq, questionIdx) => {
              const globalIndex = `${categoryIdx}-${questionIdx}`;
              const isOpen = openIndex === globalIndex;
              
              return (
                <div 
                  key={questionIdx}
                  className="bg-white rounded-lg shadow-sm border border-[#d1ddd1] overflow-hidden"
                >
                  <button
                    onClick={() => toggleQuestion(globalIndex)}
                    className="w-full text-left p-4 hover:bg-[#f8faf8] transition-colors flex justify-between items-center"
                  >
                    <span className="font-semibold text-[#4e5a4c] pr-4">
                      {faq.question}
                    </span>
                    <span className={`text-[#6b7966] text-xl transition-transform ${isOpen ? 'rotate-180' : ''}`}>
                      ▼
                    </span>
                  </button>
                  
                  {isOpen && (
                    <div className="p-4 pt-0 border-t border-[#d1ddd1]">
                      <p className="text-gray-700 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Still Have Questions */}
      <div className="bg-[#f8faf8] border border-[#d1ddd1] rounded-lg p-6 mt-8">
        <h2 className="text-xl font-bold text-[#4e5a4c] mb-3">Still Have Questions?</h2>
        <p className="text-gray-700 mb-4">
          Can't find what you're looking for? We're here to help!
        </p>
        <Link 
          to="/contact"
          className="inline-block bg-[#6b7966] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#5d6a5b] transition-colors"
        >
          📧 Contact Us
        </Link>
      </div>

      {/* Additional Resources */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <div className="bg-white border border-[#d1ddd1] rounded-lg p-4">
          <h3 className="font-semibold text-[#4e5a4c] mb-2">📚 Documentation</h3>
          <p className="text-sm text-gray-600 mb-3">
            Detailed guides and tutorials for using PlasAnn
          </p>
          <a href="#" className="text-[#6b7966] hover:text-[#4e5a4c] text-sm font-medium hover:underline">
            View Documentation →
          </a>
        </div>
        
        <div className="bg-white border border-[#d1ddd1] rounded-lg p-4">
          <h3 className="font-semibold text-[#4e5a4c] mb-2">🔬 Research</h3>
          <p className="text-sm text-gray-600 mb-3">
            Learn about the science behind PlasAnn
          </p>
          <a href="#" className="text-[#6b7966] hover:text-[#4e5a4c] text-sm font-medium hover:underline">
            Read Publications →
          </a>
        </div>
      </div>
    </div>
  );
}

export default FAQ;